/**
 * PATH: apps/hr/src/config/tabs.ts
 * PURPOSE: HR Hub tab configuration for the shared tab system.
 */

import { createTabStore, type TabNavigationConfig } from '@quotech/ui'
import {
  LayoutDashboard,
  Users,
  UserCircle,
  DollarSign,
  FileText,
  LifeBuoy,
  Network,
  BarChart3,
} from 'lucide-react'

// ─── Tab Store ──────────────────────────────────────────────
export const useHrTabStore = createTabStore({
  hubId: 'hr',
  defaultTab: { path: '/employees', title: 'Employees', iconKey: 'Users' },
})

// ─── Icon Map ───────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Users,
  UserCircle,
  DollarSign,
  FileText,
  LifeBuoy,
  Network,
  BarChart3,
}

// ─── Route Map ──────────────────────────────────────────────
const ROUTE_MAP: Record<string, { title: string; iconKey: string }> = {
  '/': { title: 'Dashboard', iconKey: 'LayoutDashboard' },
  '/dashboard': { title: 'Dashboard', iconKey: 'LayoutDashboard' },
  '/employees': { title: 'Employees', iconKey: 'Users' },
  '/payroll': { title: 'Payroll', iconKey: 'DollarSign' },
  '/documents': { title: 'Documents', iconKey: 'FileText' },
  '/support': { title: 'Support', iconKey: 'LifeBuoy' },
  '/org-chart': { title: 'Org Chart', iconKey: 'Network' },
  '/reports': { title: 'Reports', iconKey: 'BarChart3' },
}

// ─── Config ─────────────────────────────────────────────────
export const HR_TAB_CONFIG: TabNavigationConfig = {
  useStore: useHrTabStore,
  routeMap: ROUTE_MAP,
  dynamicRoutes: [
    {
      pattern: /^\/employees\/(.+)/,
      getMetadata: (path) => ({
        title: `Employee #${path.split('/')[2]}`,
        iconKey: 'UserCircle',
      }),
    },
    {
      pattern: /^\/payroll\/(.+)/,
      getMetadata: (path) => ({
        title: `Pay Run #${path.split('/')[2]}`,
        iconKey: 'DollarSign',
      }),
    },
    {
      pattern: /^\/documents\/(.+)/,
      getMetadata: (path) => ({
        title: `Document #${path.split('/')[2]}`,
        iconKey: 'FileText',
      }),
    },
    {
      pattern: /^\/support\/(.+)/,
      getMetadata: (path) => ({
        title: `Ticket #${path.split('/')[2]}`,
        iconKey: 'LifeBuoy',
      }),
    },
  ],
  excludedPaths: ['/sso', '/login', '/settings'],
  defaultNewTabPath: '/employees',
  iconMap: ICON_MAP,
}
